/**
 * Threshold alert engine (Vigía P-6), a TS mirror of
 * `native-agent::alerts`. Evaluates an inventory against default rules and
 * returns the triggered alerts (full disk, temperature, memory).
 */

import {
  type AlertSeverity,
  type FleetAlert,
  type Inventory,
  maxTemperatureC,
  memoryUsedPercent,
  worstDiskUsedPercent,
} from "./types";

type Metric = "worst_disk_used_percent" | "max_temperature_c" | "memory_used_percent";
type Comparison = "gte";

interface AlertRule {
  id: string;
  metric: Metric;
  op: Comparison;
  threshold: number;
  severity: AlertSeverity;
  message: string;
}

/** Default rules (same thresholds as `native-agent::alerts::default_rules`). */
export function defaultRules(): AlertRule[] {
  return [
    {
      id: "disk-critical",
      metric: "worst_disk_used_percent",
      op: "gte",
      threshold: 90,
      severity: "critical",
      message: "Disco casi lleno (>90%)",
    },
    {
      id: "temp-critical",
      metric: "max_temperature_c",
      op: "gte",
      threshold: 85,
      severity: "critical",
      message: "Temperatura elevada (>85°C)",
    },
    {
      id: "memory-warning",
      metric: "memory_used_percent",
      op: "gte",
      threshold: 95,
      severity: "warning",
      message: "Memoria casi agotada (>95%)",
    },
  ];
}

const extractors: Record<Metric, (inv: Inventory) => number | null> = {
  worst_disk_used_percent: worstDiskUsedPercent,
  max_temperature_c: maxTemperatureC,
  memory_used_percent: memoryUsedPercent,
};

function extract(metric: Metric, inv: Inventory): number | null {
  return extractors[metric](inv);
}

/** Evaluates the rules against the inventory and returns the triggered alerts. */
export function evaluate(inv: Inventory, rules: AlertRule[] = defaultRules()): FleetAlert[] {
  const alerts: FleetAlert[] = [];
  for (const rule of rules) {
    const value = extract(rule.metric, inv);
    if (value === null) {
      continue;
    }
    if (value >= rule.threshold) {
      alerts.push({
        rule_id: rule.id,
        severity: rule.severity,
        metric: rule.metric,
        value,
        threshold: rule.threshold,
        message: rule.message,
      });
    }
  }
  return alerts;
}
